import React, { useEffect, useMemo, useRef, useState } from "react";

import { Editor } from "@hugerte/hugerte-react";
import { useTranslation } from "react-i18next";

import { usePermissions } from "hooks/permission";

import { TranscriptionStatus } from "features/fullview/RightTemplate/utils";
import { LoadSpinner } from "features/LoadSpinner";

import Button from "components/Button";
import { DropdownGlobal } from "components/DropdownGlobal";

import { customerTemplateService } from "services/costumerTemplate";
import { editorInit } from "services/utils/editor.config";

import { FORMS_HEARING } from "utils/entities/permission";

import { collapseSpeakerLabels } from "./collapseSpeakerLabels";
import { ITranscription } from "./interface";
import {
  Container,
  FooterContainer,
  ButtonContainer,
  ContainerTiny,
  OptionListContainer,
  ContainerItem,
  Circle,
  ItemTitle,
  ContainerTemplate,
  StatusContainer,
  ContainerStatus,
} from "./styles";

const optionList = [
  { id: 1, value: "Manual", type: "manual" },
  { id: 2, value: "Automática", type: "automatico" },
];

const Transcription: React.FC<ITranscription> = ({
  buttonTitle,
  buttonFunction,
  status: statusProp,
  ataRef,
  transcription = [],
  modelList,
  transcriptionSelect,
  setTranscriptionSelect,
  selectTemplate,
  setSelectTemplate,
  showModelInput,
  setShowModelInput,
  hearingFileId,
  tryAgain,
  loadingTranscription,
}) => {
  const { t } = useTranslation();
  const status = statusProp ?? 0;
  const isFinished = status === 6;
  const isError = status > 6 && status < 15;

  const [editorInitialContent, setEditorInitialContent] = useState("");
  const { export: exportDownloads } = usePermissions(
    FORMS_HEARING.HEARING_FILE,
  );

  const blockChangeTemplate = useRef(false);
  const prevTemplateRef = useRef(selectTemplate);

  const tinymceInit = useMemo(
    () => ({
      ...editorInit(exportDownloads, true),
      content_style: `
      body { margin: 0; padding: 0; }
      p { margin: 0 0 4px; line-height: 1.4; }
    `,
    }),
    [exportDownloads],
  );

  useEffect(() => {
    if (!transcription.length) return;
    const first = transcription[0];
    const html =
      transcriptionSelect !== "manual" && first.textPlain
        ? collapseSpeakerLabels(first.textPlain)
        : first.content || "";

    setEditorInitialContent(html);
    ataRef.current?.setContent(html);
    blockChangeTemplate.current = true;
  }, [transcription, transcriptionSelect, ataRef]);

  useEffect(() => {
    if (
      prevTemplateRef.current !== selectTemplate &&
      !transcription.length &&
      selectTemplate?.id
    ) {
      (async () => {
        const { body } = await customerTemplateService.SetKeywordsByHearingFile(
          {
            hearingFileId,
            customerTemplateId: selectTemplate.id,
          },
        );
        setEditorInitialContent(body);
        ataRef.current?.setContent(body);
        blockChangeTemplate.current = true;
      })();
      prevTemplateRef.current = selectTemplate;
    }
  }, [selectTemplate, transcription.length, hearingFileId, ataRef]);

  const canClick =
    transcriptionSelect === "manual" ||
    (!transcription.length &&
      transcriptionSelect === "automatico" &&
      !!selectTemplate) ||
    (transcription.length > 0 && isFinished);
  const isDisabled = !canClick && !(isFinished || isError);

  const editorNode = useMemo(() => {
    return (
      <Editor
        onInit={(_: unknown, editor) => {
          ataRef.current = editor;
          editor.setContent(editorInitialContent);
        }}
        init={tinymceInit}
        initialValue={editorInitialContent}
        onCopy={(e: ClipboardEvent) => e.preventDefault()}
        onCut={(e: ClipboardEvent) => e.preventDefault()}
        onKeyDown={(e: React.KeyboardEvent) => {
          if ((e.ctrlKey || e.metaKey) && e.key === "c") {
            e.preventDefault();
            alert("Não é possível copiar esse texto");
          }
        }}
      />
    );
  }, [tinymceInit, editorInitialContent, ataRef]);

  return (
    <Container>
      <ContainerTiny showModelInput={showModelInput}>
        {loadingTranscription && <LoadSpinner loading={loadingTranscription} />}

        {transcription.length === 0 && (
          <StatusContainer>
            <OptionListContainer>
              {optionList.map((item) => (
                <ContainerItem
                  key={item.id}
                  onClick={() => {
                    setTranscriptionSelect(
                      transcriptionSelect === item.type ? "" : item.type,
                    );
                    setShowModelInput(item.type === "automatico");
                  }}
                >
                  <Circle selected={transcriptionSelect === item.type} />
                  <ItemTitle>{item.value}</ItemTitle>
                </ContainerItem>
              ))}
            </OptionListContainer>
          </StatusContainer>
        )}

        {showModelInput && (
          <ContainerTemplate>
            <DropdownGlobal
              labelName="Modelo"
              options={modelList}
              defaultValue={selectTemplate?.name || "Selecione o modelo"}
              showDefaultValue
              width="320px"
              setSelected={setSelectTemplate}
              wantAnObject
              disabled={blockChangeTemplate.current}
            />
          </ContainerTemplate>
        )}

        {isFinished ? (
          editorNode
        ) : (
          <StatusContainer>
            <ContainerStatus>{TranscriptionStatus(status, t)}</ContainerStatus>
          </StatusContainer>
        )}
      </ContainerTiny>

      <FooterContainer>
        <ButtonContainer>
          <Button
            disabled={isDisabled}
            onClick={isError ? tryAgain : buttonFunction}
          >
            {isError ? "Tentar novamente" : buttonTitle}
          </Button>
        </ButtonContainer>
      </FooterContainer>
    </Container>
  );
};

export default Transcription;
